/**
 * useClientNames — canonical client-name resolver hook (2026-05-22, Anil).
 *
 * Why this exists: a project doc carries `clientName` only as a DISPLAY
 * CACHE. That cache can drift from the `clients/{id}` master — one of a
 * client's projects can hold a project/site descriptor (or a stale name)
 * in its `clientName` field while the master record holds the real,
 * current client name. AVBP_DATA_MODEL_IA_SPEC_v1 is explicit: the client
 * name has ONE canonical source — the `clients/{id}` master. A screen that
 * renders `project.clientName` raw will show the same client under two
 * different names on two different screens (the owner's `C00464` bug).
 *
 * This hook streams the `clients/` masters ONCE and hands back a bound
 * `ClientNameResolver`: given a project's `clientId` (+ its cached
 * `clientName` as the fallback) it returns the MASTER's name whenever a
 * master exists, falling back to the project cache only for a legacy
 * project whose client has no master doc yet.
 *
 * Cheap: `listenClients` reads from the same persistent Firestore cache the
 * Clients screen already warmed; the masters collection is small and the
 * subscription is shared across React's render tree per mount.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  clientNameResolver,
  listenClients,
  type ClientNameResolver,
  type LiveClient,
} from './projectStore';

export interface UseClientNames {
  /** clientId (+ fallback) → canonical client name (master wins). */
  resolveName: ClientNameResolver;
  /** Raw master list, in case a caller needs more than the name. */
  masters: LiveClient[];
  /** True once the first `clients/` snapshot has arrived. */
  ready: boolean;
}

/**
 * Subscribe to the `clients/` masters and expose a canonical name resolver.
 * Before the first snapshot lands, `resolveName` simply returns the
 * fallback (the project's cached name) — so a screen renders immediately
 * and self-corrects to the master name the instant the stream is ready.
 */
export function useClientNames(): UseClientNames {
  const [masters, setMasters] = useState<LiveClient[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const unsub = listenClients(setMasters, {
      onReady: () => setReady(true),
      onErr: () => setReady(true), // a read failure must not wedge the UI
    });
    return unsub;
  }, []);

  const resolveName = useMemo(
    () => clientNameResolver(masters),
    [masters],
  );

  return { resolveName, masters, ready };
}
